import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Input, InputNumber, Button, Typography, Space, Alert, Divider } from 'antd';
import { AppapproDTO } from '@/shared/api/model';
import LoanScheduleComponent from './LoanScheduleComponent';
import { formatCurrency, parseJavaDateArray } from "@/shared/utils/formatters";
import { useLoanSchedule } from '@/hooks/loans/useLoans';
import { LoanFeeBreakdown } from './application/LoanFeeBreakdown';
import { useLoanApprovalData } from './LoanApprovalDataProvider';
import { useProductPropertiesById } from '@/modules/loan-product-settings/hooks/useProductProperties';

const { Title, Text } = Typography;

interface LoanSupervisorReviewViewProps {
  data?: any;
  formikProps?: any;
  onSubmit?: (values: any) => void;
}

/**
 * LoanSupervisorReviewView component that allows loan officers to review loan applications
 * and update loan amount and period with recommended values.
 */
const LoanSupervisorReviewView: React.FC<LoanSupervisorReviewViewProps> = ({ data, formikProps, onSubmit }) => {
  const [form] = Form.useForm();
  const values = formikProps?.values || data || {};
  const [valueData, setValueData] = useState<AppapproDTO>(values.valueData as AppapproDTO);

  const { data: productPropertiesResponse } = useProductPropertiesById((valueData as any)?.glNo ?? undefined);
  const installmentRoundingPrecision = (productPropertiesResponse?.data as any)?.installmentRoundingPrecision ?? null;
  const [updatedSchedule, setUpdatedSchedule] = useState<any>(null);
  const [calculatingSchedule, setCalculatingSchedule] = useState<boolean>(false);
  const [modifiedAppapproDTO, setModifiedAppapproDTO] = useState<AppapproDTO | null>(null);
  
  // Get loan balance data from context
  const { loanBalanceData, isBalanceLoading, balanceError } = useLoanApprovalData();

  // Use the existing loan schedule hook
  // @ts-ignore
  const [scheduleQuery, refetchSchedule] = useLoanSchedule(modifiedAppapproDTO);

  useEffect(() => {
    if (values.valueData) {
      setValueData(values.valueData as AppapproDTO);
      form.setFieldsValue({
        recommendedLoanAmt: values.valueData.recommendedLoanAmt || values.valueData.loanAmount,
        recommendedLnPeriod: values.valueData.recommendedLnPeriod || values.valueData.lnPeriod,
      });
    }
  }, [values.valueData, form]);

  // Update schedule data when query completes
  useEffect(() => {
    if (scheduleQuery.isSuccess && scheduleQuery.data) {
      setUpdatedSchedule(scheduleQuery.data);
      setCalculatingSchedule(false);
      
      // Update installment amount in formik values
      // @ts-ignore
      if (formikProps?.setFieldValue && scheduleQuery.data.scheduleDTO?.length > 0) {
        // @ts-ignore
        const installmentAmount = scheduleQuery.data.scheduleDTO[0].totalDue ;
        formikProps.setFieldValue('valueData.newInstallment', installmentAmount);
      }

    }
  }, [scheduleQuery.isSuccess, scheduleQuery.data, formikProps]);

  const handleValuesChange = (changedValues: any) => {
    // Update form values, but don't trigger schedule calculation yet
    if (formikProps?.setFieldValue) {
      Object.keys(changedValues).forEach(key => {
        formikProps.setFieldValue(`valueData.${key}`, changedValues[key]);
      });
    }
  };


  const handleCalculate = async () => {
    try {
      const formValues = form.getFieldsValue();
      setCalculatingSchedule(true);
      
      // Update formik values with recommendations
      if (formikProps?.setFieldValue) {
        formikProps.setFieldValue('valueData.recommendedLoanAmt', formValues.recommendedLoanAmt);
        formikProps.setFieldValue('valueData.recommendedLnPeriod', formValues.recommendedLnPeriod);
      }
      
      // Create a modified DTO for schedule calculation
      const modifiedDTO: AppapproDTO = {
        ...valueData,
        loanAmount: formValues.recommendedLoanAmt,
        lnPeriod: formValues.recommendedLnPeriod
      };
      
      // Set the modified DTO to trigger the useLoanSchedule hook
      setModifiedAppapproDTO(modifiedDTO);
      
    } catch (error) {
      console.error('Failed to calculate loan schedule:', error);
      setCalculatingSchedule(false);
    }
  };

  // @ts-ignore
  return (
    <Card bordered={false} className="loan-officer-view">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Title level={4}>Loan Application Review</Title>
          <Text type="secondary">
            Review the loan application and update the recommended loan amount and period if necessary.
          </Text>
        </Col>

        <Col span={24}>
          <Card title="Loan Details" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Loan ID:</Text> <Text>{valueData?.loanId}</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Customer:</Text> <Text>{valueData?.name}</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Application Date:</Text> <Text>{valueData?.appliDate ? parseJavaDateArray(valueData.appliDate)?.toLocaleDateString() || 'N/A' : 'N/A'}</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Applied Amount:</Text> <Text>{formatCurrency(valueData?.loanAmount)}</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Applied Period:</Text> <Text>{valueData?.lnPeriod} {Number(valueData?.lnPeriod) === 1 ? 'month' : 'months'}</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Interest Rate:</Text> <Text>{valueData?.intRate}%</Text>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Supervisor Review & Final Recommendations" size="small">
            <Alert
              message="Important"
              description="As a supervisor, you can modify the recommended loan amount and period. Any changes will generate a new loan schedule and the borrower will need to sign a new deduction form."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form 
              form={form}
              layout="vertical"
              onValuesChange={handleValuesChange}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="recommendedLoanAmt"
                    label="Recommended Loan Amount"
                    rules={[{ required: true, message: 'Please enter recommended loan amount' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      // @ts-ignore
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      min={0}
                      placeholder="Enter recommended loan amount"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="recommendedLnPeriod"
                    label="Recommended Loan Period (months)"
                    rules={[{ required: true, message: 'Please enter recommended loan period' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={60}
                      placeholder="Enter recommended loan period in months"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        {/* Fee Breakdown */}
        {valueData && (
          <Col span={24}>
            <Card title="Fee Breakdown & Customer Net Amount" size="small">
              <Alert
                message="Fee Information"
                description={`Review the fees that will be deducted and the net amount the customer will receive. ${isBalanceLoading ? 'Loading loan balance...' : loanBalanceData ? 'Existing loan balance will be settled from the loan amount.' : 'For top-up loans, existing loan balance will be settled from the loan amount.'}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <LoanFeeBreakdown
                glNo={valueData.glNo}
                loanAmount={form.getFieldValue('recommendedLoanAmt') || valueData.loanAmount}
                loanPeriod={form.getFieldValue('recommendedLnPeriod') || valueData.lnPeriod}
                showTitle={false}
                compact={false}
                loanBalanceData={loanBalanceData}
                netAmountLabel="Net Amount Customer Will Receive"
              />
            </Card>
          </Col>
        )}

        {(valueData || updatedSchedule) && (
          <Col span={24}>
            <Divider orientation="left">Loan Repayment Schedule</Divider>
            {updatedSchedule ? (
              <>
                <Alert
                  message="Updated Schedule Preview"
                  description={`This schedule is based on the recommended loan amount of ${formatCurrency(form.getFieldValue('recommendedLoanAmt'))} 
                  and period of ${form.getFieldValue('recommendedLnPeriod')} months. 
                  ${updatedSchedule.repaymentPlan?.length > 0 ? 
                    `Monthly installment: ${formatCurrency(updatedSchedule.repaymentPlan[0].principal + updatedSchedule.repaymentPlan[0].interest)}` : ''}`}
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <LoanScheduleComponent
                    // @ts-ignore
                  appapproDTO={{
                    ...valueData,
                    loanAmount: form.getFieldValue('recommendedLoanAmt'),
                    lnPeriod: form.getFieldValue('recommendedLnPeriod'),
                    newInstallment: updatedSchedule.repaymentPlan?.length > 0 ? 
                      updatedSchedule.repaymentPlan[0].principal + updatedSchedule.repaymentPlan[0].interest : 0
                  }}
                  installmentRoundingPrecision={installmentRoundingPrecision}
                />
              </>
            ) : (
              <>
                <Alert
                  message="Original Schedule Preview"
                  description="This schedule is based on the original loan parameters. Click 'Calculate Updated Schedule' to see changes based on your recommendations."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                {/*@ts-ignore*/}
                <LoanScheduleComponent appapproDTO={valueData} installmentRoundingPrecision={installmentRoundingPrecision} />
              </>
            )}
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default LoanSupervisorReviewView;
